/**
 * ReturnTickets — table of tickets that contain returned items.
 * Props: categories
 */
import React, { useState, useMemo } from 'react';
import { fmt$ } from '../../../utils/formatUtils';
import { fmtTimeCst } from '../../../utils/cstUtils';

export default function ReturnTickets({ categories }) {
  const [expanded, setExpanded] = useState(null);

  const returnCats = useMemo(
    () => new Set(categories.filter(c => c.returnQty > 0).map(c => c.name)),
    [categories]
  );

  const tickets = useMemo(() => {
    const seen = new Set(), list = [];
    categories.forEach(cat => {
      if (!returnCats.has(cat.name)) return;
      (cat.ticketList || []).forEach(tkt => {
        if (!seen.has(tkt.id)) {
          seen.add(tkt.id);
          list.push({ ...tkt, catName: cat.name });
        }
      });
    });
    return list.sort((a, b) => new Date(b.time) - new Date(a.time));
  }, [categories, returnCats]);

  if (!tickets.length) return <p className="sq__empty">No ticket details available.</p>;

  return (
    <div className="sq__table-wrap">
      <table className="sq__table sq__tkt-tbl">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th>Time (CST)</th>
            <th>Ticket #</th>
            <th>Status</th>
            <th>Category</th>
            <th className="sq__num">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map(tkt => (
            <React.Fragment key={tkt.id + tkt.catName}>
              <tr
                className={`sq__tkt-row sq__tkt-row--ret${expanded === tkt.id ? ' open' : ''}`}
                onClick={() => setExpanded(expanded === tkt.id ? null : tkt.id)}
              >
                <td className="sq__tkt-toggle">{expanded === tkt.id ? '\u25BE' : '\u25B8'}</td>
                <td className="sq__tkt-time">{fmtTimeCst(tkt.time)}</td>
                <td className="sq__tkt-id">{tkt.shortId}</td>
                <td>
                  <span className={`sq__badge sq__badge--${tkt.status.toLowerCase()}`}>
                    {tkt.status === 'OPEN' ? '🟡 Open' : '🟢 Done'}
                  </span>
                </td>
                <td className="sq__cats-cell">{tkt.catName}</td>
                <td className="sq__num sq__revenue">{fmt$(tkt.revenue)}</td>
              </tr>
              {expanded === tkt.id && (
                <tr className="sq__tkt-expand">
                  <td colSpan={6}>
                    <div className="sq__tkt-lines">
                      {tkt.lines.map((l, j) => (
                        <div key={j} className="sq__tkt-line">
                          <span className="sq__tkt-lname">{l.name}</span>
                          <span className="sq__tkt-lqty-badge">{l.qty}</span>
                          <span className="sq__tkt-lrev">{fmt$(l.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
